import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from "@adgate/common/pipes/object-desc-pipe";
import { CampaignFilter } from '@adgate/modules/streaming/types/campaign-filter.type';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import moment from 'moment';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { Status } from '@adgate/common/types/status.type';
import { CommercialOffice } from '@adgate/common/types/commercial-office.type';
import { Agency } from '@adgate/common/types/agency.type';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-streaming-campaign-filters-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    ObjectDescPipe
  ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: AdGateConstants.ADGATE_DATE_FORMATS }
  ],
  templateUrl: './streaming-campaign-filters-dialog-component.html',
  styleUrl: './streaming-campaign-filters-dialog-component.scss',
})
export class StreamingCampaignFiltersDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<StreamingCampaignFiltersDialogComponent> = inject(MatDialogRef<StreamingCampaignFiltersDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogService: DialogService = inject(DialogService);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);
  private currentFilter: CampaignFilter = inject<CampaignFilter>(MAT_DIALOG_DATA);

  public comparatorService: ComparatorService = inject(ComparatorService);

  filtersForm!: FormGroup;
  orderStatuses: Status[] = [];
  commercialOffices: CommercialOffice[] = [];
  agencies: Agency[] = [];

  constructor()
  {
    this.filtersForm = this.fb.group({
      campaignTitle: "",
      brand: "",
      commercialOffice: [],
      agency: [],
      orderStatus: [],
      createdFrom: "",
      createdTo: "",
      resultLimit: 100
    });
  }

  ngOnInit(): void
  {
    this.dialogService.showProgress();
    forkJoin([
      this.commonObjectService.getStatuses(Campaign.OBJECT_TYPE),
      this.commonObjectService.getCommercialOffices(null),
      this.commonObjectService.getAgencies(null)  
    ]).subscribe({
      next: (r:RESTApplicationResponse[]) => {
        this.orderStatuses = r[0].data;
        this.commercialOffices = r[1].data;
        this.agencies = r[2].data;

        // Populate form with current filter values
        this.filtersForm.get('campaignTitle')?.setValue(this.currentFilter.title);
        this.filtersForm.get('brand')?.setValue(this.currentFilter.brand);
        this.filtersForm.get('commercialOffice')?.setValue(this.currentFilter.commercialOffice);
        this.filtersForm.get('agency')?.setValue(this.currentFilter.agency);
        this.filtersForm.get('orderStatus')?.setValue(this.currentFilter.status);
        this.filtersForm.get('createdFrom')?.setValue(this.currentFilter.createdFrom ? moment(this.currentFilter.createdFrom) : null);
        this.filtersForm.get('createdTo')?.setValue(this.currentFilter.createdTo ? moment(this.currentFilter.createdTo) : null);
        this.filtersForm.get('resultLimit')?.setValue(this.currentFilter.limit);

        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  onClear(): void
  {
    this.filtersForm.reset({ resultLimit: 100 });
  }

  onApply(): void
  {
    this.currentFilter.title = this.filtersForm.get('campaignTitle')?.value;
    this.currentFilter.brand = this.filtersForm.get('brand')?.value;
    this.currentFilter.status = this.filtersForm.get('orderStatus')?.value;
    this.currentFilter.commercialOffice = this.filtersForm.get('commercialOffice')?.value;
    this.currentFilter.agency = this.filtersForm.get('agency')?.value;
    this.currentFilter.limit = this.filtersForm.get('resultLimit')?.value;

    const createdFrom = this.filtersForm.get('createdFrom')?.value;
    const createdTo = this.filtersForm.get('createdTo')?.value;
    this.currentFilter.createdFrom = createdFrom ? createdFrom.unix() * 1000 : null;
    this.currentFilter.createdTo = createdTo ? createdTo.unix() * 1000 : null;

    this.dialogRef.close(this.currentFilter);
  }
}
